import React, { useCallback, useMemo, useState } from 'react';
import { AskRequest, AskRequestPriorities } from '@hone-automation/common';
import { prefixClass } from 'lib/utils';
import IconButton from 'presentation/components/KSQ2/Common/IconButton';
import { IconKaiSynth, IconPromote, IconMoreVertical, IconReopen } from 'components/Icons';
import { Dropdown, DropdownItemConfig } from 'presentation/components/KSQ2/Common/Dropdown';
import { usePatchQueueRequestMutation } from 'hooks/queue/';
import { QueueView } from '@hone-automation/common';
import { useQueueContext } from 'presentation/components/Queue/QueueContext';
import { useQueuePanel } from 'presentation/components/Queue/common/QueuePanel';
import { useConversationPanelContext } from '../../../../ConversationPanelContext';
import type { ConversationSidePanel } from '../../../../ConversationPanelContext';
import { useQueryState } from 'hooks/useQueryState';
import { getRequestActionVisibility } from 'presentation/components/KSQ2/utils/actionVisibility';
import { ReopenDialog } from '../ActionsDropdown/ReopenDialog';
import './HeaderControls.scss';

interface HeaderControlsProps {
  request: AskRequest;
  secondPhase?: boolean;
  canManageQueue?: boolean;
  /** Optional amount to display (used in DataRowModal context) */
  amount?: string;
  /** Whether to show the status label (For Me, For Client, Resolved) */
  showStatusLabel?: boolean;
  /** Whether this request is a Group (hides blocker toggle) */
  isGroup?: boolean;
}

/**
 * Get the display label for the request status
 */
const getStatusLabel = (status: string): { label: string; className: string } => {
  switch (status) {
    case 'Open':
    case 'Information Needed':
      return { label: 'For Me', className: 'for-me' };
    case 'In Progress':
      return { label: 'For Client', className: 'for-client' };
    case 'Closed':
    case 'Categorized':
      return { label: 'Resolved', className: 'resolved' };
    default:
      return { label: status, className: '' };
  }
};

/**
 * HeaderControls - Container for the three action buttons on the right side of the header
 * - Summarize button (AI summary)
 * - Priority toggle (blocker/normal)
 * - More options menu
 *
 * Works in two modes:
 * - Queue mode: Uses QueueContext for state management
 * - DataRowModal mode: Uses ConversationPanelContext callbacks
 */
export const HeaderControls: React.FC<HeaderControlsProps> = ({
  request,
  secondPhase,
  canManageQueue,
  amount,
  showStatusLabel = false,
  isGroup = false,
}) => {
  const prefix = prefixClass('conversation-header-controls');
  const { dispatch, state } = useQueueContext();
  const { requests, activeView } = state;
  const { openPanel } = useQueuePanel();
  const [, setRequestId] = useQueryState('requestId');
  const isBlocker = request.priority === 'Blocker';
  const { showBlockerToggle, showReopen } = getRequestActionVisibility(request, { canManageQueue });
  const [isReopenOpen, setIsReopenOpen] = useState(false);

  // Get ConversationPanelContext for standalone mode (DataRowModal)
  const { mode, onRequestUpdate, refetchFlaggedTransactions, openSidePanel } = useConversationPanelContext();

  const statusInfo = useMemo(() => getStatusLabel(request.status), [request.status]);
  const isStandaloneMode = mode === 'dataRowModal';

  /**
   * Highlight a filter with drop animation, then auto-clear
   */
  const highlightFilter = useCallback(
    (targetFilter: QueueView) => {
      dispatch({ type: 'HIGHLIGHT_FILTER', payload: targetFilter });
      setTimeout(() => {
        dispatch({ type: 'HIGHLIGHT_FILTER', payload: null });
      }, 500);
    },
    [dispatch]
  );

  /**
   * Select the next item in the current view after current one moves out
   */
  const selectNextInView = useCallback(
    (excludeId: string, wasBlocker: boolean) => {
      if (!activeView) return;

      // Determine where the item moved to
      const targetFilter: QueueView = wasBlocker ? 'My Team' : 'Blockers';

      // If we're in the target view, item stays visible - just update it
      if (activeView === targetFilter) return;

      // Item moved out - find remaining requests in current view
      const remainingInView = requests.filter(req => {
        if (req.id === excludeId) return false;
        // Check if request belongs to current view
        if (activeView === 'Blockers') return req.priority === 'Blocker';
        // Other views exclude blockers
        return req.priority !== 'Blocker';
      });

      const nextRequest = remainingInView[0] || null;
      dispatch({ type: 'SET_SELECTED_ITEM', payload: nextRequest });
      setRequestId(nextRequest?.id || undefined);
    },
    [activeView, requests, dispatch, setRequestId]
  );

  const selectNextClosed = useCallback(
    (excludeId: string) => {
      if (activeView !== 'closed') return;
      const remaining = requests.filter(req => req.id !== excludeId && req.status === 'Closed');
      const nextRequest = remaining[0] || null;
      dispatch({ type: 'SET_SELECTED_ITEM', payload: nextRequest });
      setRequestId(nextRequest?.id || undefined);
    },
    [activeView, requests, dispatch, setRequestId]
  );

  // Priority toggle mutation
  const priorityMutation = usePatchQueueRequestMutation(data => {
    if (data) {
      // Standalone mode: use callbacks instead of QueueContext
      if (isStandaloneMode) {
        onRequestUpdate?.(data);
        refetchFlaggedTransactions?.();
        return;
      }

      // Queue mode: Update requests in context
      dispatch({
        type: 'SET_REQUESTS',
        payload: requests.map(req => (req.id === data.id ? data : req)),
      });

      // Determine target filter and highlight
      const targetFilter: QueueView = data.priority === 'Blocker' ? 'Blockers' : 'My Team';
      highlightFilter(targetFilter);

      // Check if item moved out of current view
      const movedOutOfView = activeView && targetFilter !== activeView;

      if (movedOutOfView) {
        // Select next item - pass wasBlocker to know where it came from
        selectNextInView(data.id, isBlocker);
      } else {
        // Stay on the updated item
        dispatch({
          type: 'SET_SELECTED_ITEM',
          payload: data,
        });
      }
    }
  });

  const reopenMutation = usePatchQueueRequestMutation(data => {
    if (data) {
      dispatch({
        type: 'SET_REQUESTS',
        payload: requests.map(req => (req.id === data.id ? data : req)),
      });

      if (activeView === 'closed') {
        selectNextClosed(data.id);
      } else {
        dispatch({ type: 'SET_SELECTED_ITEM', payload: data });
      }
    }
  });

  // Open AI summarize panel
  const handleSummarize = () => {
    if (isStandaloneMode && openSidePanel) {
      const summaryPanel: ConversationSidePanel = 'Summary';
      openSidePanel(summaryPanel);
      return;
    }
    openPanel('summarizeRequest', {
      data: { request },
      title: 'Request Summary',
      titleIcon: <IconKaiSynth />,
      showExplicitCloseButton: true,
    });
  };

  const handleTogglePriority = () => {
    const nextPriority = isBlocker ? AskRequestPriorities.Normal : AskRequestPriorities.Blocker;
    priorityMutation.mutate({ id: request.id, priority: nextPriority, locationId: request.locationId });
  };

  const handleReopen = () => {
    if (!request.canReopen) return;
    setIsReopenOpen(true);
  };

  const handleReopenSubmit = ({ comment }: { comment: string }) => {
    reopenMutation.mutate({ id: request.id, status: 'Open', comment });
    setIsReopenOpen(false);
  };

  // TODO: Define actual menu items
  const moreOptionsItems: DropdownItemConfig[] = [
    {
      id: 'option-1',
      label: 'Option 1',
      onClick: () => {},
    },
    {
      id: 'option-2',
      label: 'Option 2',
      onClick: () => {},
    },
  ];

  const moreOptionsTrigger = (
    <IconButton
      icon={<IconMoreVertical />}
      onClick={() => {}} // Dropdown handles the click
      size="medium"
      tooltip="More options"
    />
  );

  return (
    <div className={prefix()}>
      {/* Status label and amount (shown in DataRowModal context) */}
      {showStatusLabel && (
        <div className={`${prefix('status-label')} ${prefix(`status-label--${statusInfo.className}`)}`}>
          {statusInfo.label}
        </div>
      )}
      {amount && <div className={prefix('amount')}>{amount}</div>}

      {/* Summarize button */}
      <IconButton icon={<IconKaiSynth />} onClick={handleSummarize} size="medium" tooltip="Summarize conversation" />

      {/* Priority toggle */}
      {canManageQueue && (
        <>
          {showBlockerToggle && !isGroup && (
            <IconButton
              icon={<IconPromote />}
              onClick={handleTogglePriority}
              size="medium"
              tooltip={isBlocker ? 'Remove blocker' : 'Mark as blocker'}
              selected={isBlocker}
              type="switch"
              disabled={priorityMutation.isPending}
              className="blocker-toggle"
            />
          )}
          {showReopen && (
            <IconButton
              icon={<IconReopen />}
              onClick={handleReopen}
              size="medium"
              tooltip="Reopen request"
              disabled={reopenMutation.isPending}
              className="reopen-toggle"
            />
          )}
        </>
      )}

      {/* More options dropdown */}
      {secondPhase && (
        <Dropdown
          trigger={moreOptionsTrigger}
          items={moreOptionsItems}
          position="bottom"
          alignment="end"
          offset={{ x: 0, y: 4 }}
        />
      )}

      <ReopenDialog
        isOpen={isReopenOpen}
        isPending={reopenMutation.isPending}
        onClose={() => setIsReopenOpen(false)}
        onSubmit={handleReopenSubmit}
      />
    </div>
  );
};

export default HeaderControls;
